import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

const SavedJobs = ({ limit, showViewAll = false, setActiveTab, onRefresh }) => {
  const { user } = useAuth();
  const [savedJobs, setSavedJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchSavedJobs = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('saved_jobs')
          .select(`
            *,
            job:jobs(
              *,
              company:companies(*)
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Transform data
        const transformed = data.map(item => ({
          id: item.id,
          jobId: item.job?.id,
          title: item.job?.title,
          company: item.job?.company?.name,
          companyLogo: item.job?.company?.logo_url,
          location: item.job?.location,
          locationType: item.job?.employment_type || 'Full-time',
          salary: `£${(item.job?.salary_min / 1000).toFixed(0)}k - £${(item.job?.salary_max / 1000).toFixed(0)}k`,
          savedDate: 'Recently',
          tags: item.job?.requirements?.slice(0, 3) || []
        }));

        setSavedJobs(transformed);
      } catch (err) {
        console.error('Error fetching saved jobs:', err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSavedJobs();
  }, [user]);

  const displayJobs = limit ? savedJobs.slice(0, limit) : savedJobs;

  const handleRemoveJob = async (id) => {
    try {
      const { error } = await supabase
        .from('saved_jobs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSavedJobs(savedJobs.filter((job) => job.id !== id));
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error removing saved job:', err.message);
    }
  };

  return (
    <div className="bg-card text-card-foreground rounded-2xl shadow-lg">
      <div className="px-6 py-4 border-b border-border flex justify-between items-center">
        <h3 className="text-lg font-semibold text-card-foreground">Saved Jobs</h3>
        {showViewAll &&
          <Link to="#" onClick={() => setActiveTab('saved')} className="text-sm text-primary hover:text-primary-700 transition-smooth flex items-center">
            <span>View All</span>
            <Icon name="ChevronRight" size={16} className="ml-1" />
          </Link>
        }
      </div>

      {displayJobs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 sm:p-6">
          {displayJobs.map((job) => (
            <div
              key={job.id}
              className="group relative bg-muted/30 rounded-xl p-4 hover:bg-muted/50 transition-smooth">

              <div className="absolute top-4 right-4 flex space-x-2">
                <button
                  onClick={() => handleRemoveJob(job.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-card shadow-sm text-text-secondary hover:bg-error-50 hover:text-error-600 transition-smooth"
                  aria-label="Remove job">
                  <Icon name="X" size={16} />
                </button>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 mr-4 bg-card p-1">
                  <Image
                    src={job.companyLogo}
                    alt={job.company}
                    className="w-full h-full object-cover" />
                </div>

                <div className="flex-1 min-w-0">
                  <Link to={`/job-detail-application/${job.jobId}`} className="block">
                    <h4 className="text-base font-medium text-text-primary hover:text-primary-600 transition-smooth truncate">
                      {job.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      {job.company}
                    </p>
                  </Link>

                  <div className="flex flex-wrap items-center mt-2 text-xs text-text-secondary">
                    <div className="flex items-center mr-3">
                      <Icon name="MapPin" size={14} className="mr-1" />
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center mr-3">
                      <Icon name="Building" size={14} className="mr-1" />
                      <span>{job.locationType}</span>
                    </div>
                    <div className="flex items-center">
                      <span>{job.salary}</span>
                    </div>
                  </div>



                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-text-secondary">
                      Saved {job.savedDate}
                    </span>
                    <Link
                      to={`/job-detail-application/${job.jobId}`}
                      className="inline-flex items-center text-xs font-medium text-primary hover:text-primary-700 transition-smooth">
                      <span>Apply Now</span>
                      <Icon name="ArrowRight" size={14} className="ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <div className="p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-surface rounded-full flex items-center justify-center">
            <Icon name="Bookmark" size={24} className="text-secondary-400" />
          </div>
          <h4 className="text-lg font-medium text-text-primary mb-2">No Saved Jobs</h4>
          <p className="text-sm text-text-secondary mb-4">
            You haven't saved any jobs yet. Browse jobs and save the ones you're interested in.
          </p>
          <Link to="/job-search-browse" className="btn-primary inline-flex items-center">
            <Icon name="Search" size={16} className="mr-2" />
            <span>Browse Jobs</span>
          </Link>
        </div>
      )}
    </div >);

};

export default SavedJobs;